import { ReduceStore } from "flux/utils";
import * as _ from "lodash";
import {Subject, BehaviorSubject, Observable } from "rxjs";
import LoadObject from "../../load_objects/LoadObject";
import LoadObjectState from "../../load_objects/LoadObjectState";
import { Action, ACTION_TYPE } from "../Actions";
import FixtureDetailedDataManager from "../data/FixtureDetailedDataManager";
import { WebSocketApi } from '../data/WebSocketApi';
import Dispatcher from "../Dispatcher";
import { FixtureDetailed } from "../model/FixtureDetailed";
import { ERRORS } from './../Errors';
import { LiveEvents } from './../model/LiveEvents';

interface FixtureDetailedStoreState {
  matchDataMap: Map<number, MatchData>
}

export interface MatchData {
  fixture: FixtureDetailed | undefined | null,
  liveData: Subject<LiveEvents.Event> | undefined | null | Observable<LiveEvents.Event>,
  socket: WebSocketApi | undefined | null
  // TODO Error[]?
}

const emptyState: FixtureDetailedStoreState = { matchDataMap: new Map<number, MatchData>() };

export interface StoreConfiguration {
  legacy: boolean;
}

export class FixtureDetailedStore extends ReduceStore<LoadObjectState<FixtureDetailedStoreState>, Action> {
  private config: StoreConfiguration = {legacy: true};

  constructor() {
    super(Dispatcher);
  }

  public getInitialState(): LoadObjectState<FixtureDetailedStoreState> {
    return new LoadObjectState<FixtureDetailedStoreState>(() => { });
  }

  public getStateSafely(): FixtureDetailedStoreState {
    return this.unwrap(super.getState());
  }

  public reduce(stateLoader: LoadObjectState<FixtureDetailedStoreState>, action: Action): LoadObjectState<FixtureDetailedStoreState> {
    const state = this.unwrap(stateLoader);

    switch (action.type) {
      case ACTION_TYPE.FIXTURE_START_LOAD: {
        FixtureDetailedDataManager.getFixture(action.id);
        return stateLoader;
      }

      case ACTION_TYPE.FIXTURE_LOADED: {
        const matchData = state.matchDataMap.get(action.fixture.id);
        if (matchData) {
          state.matchDataMap.set(action.fixture.id, { fixture: action.fixture, liveData: matchData!.liveData, socket: matchData.socket });
        } else {
          state.matchDataMap.set(action.fixture.id, { fixture: action.fixture, liveData: undefined, socket: undefined });
        }
        if (action.fixture.status === "Started") {
          _.defer(() => Dispatcher.dispatch({
            type: ACTION_TYPE.LIVE_DATA_START_LOAD,
            id: action.fixture.id,
            legacy: this.config.legacy
          }));
        } else if (action.fixture.status === "Ended") {
          _.defer(() => Dispatcher.dispatch({
            type: ACTION_TYPE.HISTORIC_DATA_START_LOAD,
            id: action.fixture.id,
          }));
        } else if (action.fixture.status === "Scheduled") { // CF: Simply try again... and again...
          _.defer(() => Dispatcher.dispatch({
            type: ACTION_TYPE.FIXTURE_START_LOAD,
            id: action.fixture.id,
          }));
        } else {
          throw new Error(`Unexpected fixture status (${action.fixture.status})`)  // TODO Cancelled!
        }
        return this.wrap(stateLoader, state);
      }

      case ACTION_TYPE.FIXTURE_ERROR: {
        const errorCode: number = ((((action.error as any)?.request) as XMLHttpRequest)?.status);
        let matchData: MatchData = state.matchDataMap.get(action.id)!;
        if (errorCode === 404) { // TODO more codes
          state.matchDataMap.set(action.id, { fixture: null, liveData: matchData?.liveData, socket: matchData?.socket });
        } else {
          throw action.error;  // TODO uncaught
        }
        return this.wrap(stateLoader, state);
      }

      case ACTION_TYPE.HISTORIC_DATA_START_LOAD: {
        const matchData: MatchData | undefined = state.matchDataMap.get(action.id);
        if (matchData?.liveData === undefined) {
          FixtureDetailedDataManager.getHistoricLiveData(action.id, this.config.legacy);
        } else {
          _.defer(() => Dispatcher.dispatch({
            type: ACTION_TYPE.LIVE_DATA_LOADED,
            id: action.id,
            liveData: matchData!.liveData!,
            socket: matchData!.socket
          }));
        }
        return stateLoader;
      }

      case ACTION_TYPE.HISTORIC_DATA_LOADED: {
        let matchData: MatchData = state.matchDataMap.get(action.id)!;
        matchData.liveData =  action.liveData;
        matchData.socket = undefined;
        return this.wrap(stateLoader, state);
      }

      case ACTION_TYPE.LIVE_DATA_START_LOAD: {
        const matchData: MatchData | undefined = state.matchDataMap.get(action.id);
        FixtureDetailedDataManager.getLiveData(action.id, this.config.legacy); // TODO uncaught 401 error
        return stateLoader
      }

      case ACTION_TYPE.LIVE_DATA_LOADED: {
        let matchData: MatchData = state.matchDataMap.get(action.id)!;
        matchData.liveData = action.liveData;
        matchData.socket = action.socket;
        return this.wrap(stateLoader, state);
      }

      case ACTION_TYPE.LIVE_DATA_UNSOCKET: {
        let matchData: MatchData = state.matchDataMap.get(action.id)!;
        if (matchData?.socket) {
          matchData.socket.closeSocket()
          matchData.socket = null;
          matchData.liveData = undefined;
          return this.wrap(stateLoader, state);
        }
        return stateLoader;
      }

      case ACTION_TYPE.LIVE_DATA_ERROR: case ACTION_TYPE.HISTORIC_DATA_ERROR: {
        const errorCode: number = ((((action.error as any)?.request) as XMLHttpRequest)?.status);
        const matchData: MatchData = state.matchDataMap.get(action.id)!;
        if ((errorCode === 401 || action.error.message === ERRORS.WEBSOCKET_401) || errorCode === 404) { // TODO differentiate between 401 and 404
          matchData.liveData = null;
          matchData.socket = undefined;
        } else if (500 <= errorCode && errorCode > 600) {
          matchData.liveData = undefined;
          matchData.socket = undefined;
        } else if (action.error.message === ERRORS.HISTORIC_LIVE_FIXTURE_EMPTY_DATA) {
          matchData.liveData = null;
          matchData.socket = undefined;
        } else if (action.error.message === ERRORS.WEBSOCKET_ENDED) {
          matchData.liveData = null;
          matchData.socket = undefined;
        } else {
          throw action.error;  // TODO uncaught
        }
        return this.wrap(stateLoader, state);
      }
    }
  }

  private unwrap(state: LoadObjectState<FixtureDetailedStoreState>): FixtureDetailedStoreState {
    const loadObject = state.getLoadObject();
    if (!loadObject || !loadObject.hasValue()) {
      return emptyState;
    }
    return loadObject.getValue() || emptyState;
  }

  private wrap(
    loader: LoadObjectState<FixtureDetailedStoreState>, state: FixtureDetailedStoreState): LoadObjectState<FixtureDetailedStoreState> {
    return loader.setLoadObject(LoadObject.withValue(state));
  }

  public configure(config: StoreConfiguration) {
    this.config = config;
  }
}

export default new FixtureDetailedStore();
